import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { questionnaireService, clientService, adminService, testService } from '../services/api';
import { toast } from 'sonner';
import { Upload, Sparkles, RotateCw } from 'lucide-react';
import { AutocompleteInput } from '../components/AutocompleteInput';
import { QuestionCard } from '../components/QuestionCard';
import { ValidationForm } from '../components/ValidationForm';

export const GenererQuestionnairePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const externalNom = searchParams.get('nom') || '';
  const externalPrenom = searchParams.get('prenom') || '';
  const hasExternalCandidate = !!(externalNom && externalPrenom);
  const [mode, setMode] = useState('description');
  const [loading, setLoading] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // Generic mode state
  const [fonction, setFonction] = useState('');
  const [niveau, setNiveau] = useState('');
  const [competences, setCompetences] = useState(['', '', '']);

  // Description mode state
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Save mode state
  const [typeQuestionnaire, setTypeQuestionnaire] = useState('');
  const [client, setClient] = useState('');
  const [interlocuteurClient, setInterlocuteurClient] = useState('');
  const [refAo, setRefAo] = useState('');
  const [errors, setErrors] = useState({});

  const checkDuplicate = useCallback(
    (() => {
      let timer;
      return (value) => {
        clearTimeout(timer);
        if (!value || value.length < 3) {
          setDuplicateInfo(null);
          return;
        }
        timer = setTimeout(async () => {
          try {
            const res = await adminService.checkDuplicateRefAo(value);
            setDuplicateInfo(res.data.exists ? res.data.questionnaire : null);
          } catch {}
        }, 500);
      };
    })(),
    []
  );

  const [duplicatePopup, setDuplicatePopup] = useState(null);

  // Check for existing questionnaire and show popup
  const checkAndShowDuplicate = async (refAoValue) => {
    if (!refAoValue || refAoValue.length < 2) return;
    try {
      const res = await adminService.checkDuplicateRefAo(refAoValue);
      if (res.data.exists) {
        setDuplicatePopup(res.data.questionnaire);
      }
    } catch {}
  };

  const handleRefAoChange = (val) => {
    setRefAo(val);
    checkDuplicate(val);
  };

  const handleGenerate = async () => {
    if (mode === 'generic') {
      if (!fonction || !niveau) {
        toast.error('Fonction et niveau sont obligatoires');
        return;
      }
      setLoading(true);
      try {
        const response = await questionnaireService.generateGeneric(
          fonction, niveau, competences.filter(c => c)
        );
        setGeneratedData(response.data);
        if (response.data.metadata) {
          const m = response.data.metadata;
          if (m.fonction && !fonction) setFonction(m.fonction);
          if (m.niveau && !niveau) setNiveau(m.niveau);
          if (m.client) setClient(m.client);
          if (m.ref_ao) { setRefAo(m.ref_ao); checkDuplicate(m.ref_ao); }
        }
      } catch (error) {
        toast.error('Erreur lors de la génération');
      } finally {
        setLoading(false);
      }
    } else {
      if (!description && !file) {
        toast.error('Veuillez fournir une description ou un fichier');
        return;
      }
      setLoading(true);
      try {
        const response = await questionnaireService.generateFromDescription(description, file);
        setGeneratedData(response.data);
        if (response.data.metadata) {
          const m = response.data.metadata;
          if (m.fonction) setFonction(m.fonction);
          if (m.niveau) setNiveau(m.niveau);
          if (m.client) setClient(m.client);
          if (m.ref_ao) {
            setRefAo(m.ref_ao);
            checkDuplicate(m.ref_ao);
            checkAndShowDuplicate(m.ref_ao);
          }
        }
        setTypeQuestionnaire("Appel d'offres");
      } catch (error) {
        toast.error('Erreur lors de la génération');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRegenerateQuestion = async (index) => {
    try {
      const response = await questionnaireService.regenerateQuestion(index, {
        fonction,
        niveau,
        competences: competences.filter(c => c)
      });
      const updatedQuestions = [...generatedData.questions];
      updatedQuestions[index] = response.data;
      setGeneratedData({ ...generatedData, questions: updatedQuestions });
      toast.success(`Question ${index + 1} régénérée`);
    } catch (error) {
      toast.error('Erreur lors de la régénération');
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!fonction) newErrors.fonction = true;
    if (!niveau) newErrors.niveau = true;
    if (!typeQuestionnaire) newErrors.typeQuestionnaire = true;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Vous n\'avez pas complété un champ obligatoire');
      return;
    }
    setSaving(true);
    try {
      await questionnaireService.save({
        fonction, niveau, type_questionnaire: typeQuestionnaire,
        client: client || null, interlocuteur_client: interlocuteurClient || null,
        ref_ao: refAo || null, questions: generatedData.questions,
        tags: generatedData.tags
      });
      toast.success('Questionnaire enregistré');
      navigate('/questionnaires');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleLaunchTest = async () => {
    const newErrors = {};
    if (!fonction) newErrors.fonction = true;
    if (!niveau) newErrors.niveau = true;
    if (!typeQuestionnaire) newErrors.typeQuestionnaire = true;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Vous n\'avez pas complété un champ obligatoire');
      return;
    }
    setSaving(true);
    try {
      const response = await questionnaireService.save({
        fonction, niveau, type_questionnaire: typeQuestionnaire,
        client: client || null, interlocuteur_client: interlocuteurClient || null,
        ref_ao: refAo || null, questions: generatedData.questions,
        tags: generatedData.tags
      });
      if (hasExternalCandidate) {
        const testRes = await testService.create(response.data.id, externalNom, externalPrenom);
        navigate(`/lancer-test/${response.data.id}`, { state: { testId: testRes.data.id, autoStart: true } });
      } else {
        navigate(`/lancer-test/${response.data.id}`);
      }
    } catch (error) {
      toast.error('Erreur lors du lancement');
    } finally {
      setSaving(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); };

  return (
    <Layout title="Générer un questionnaire" breadcrumbs={['Accueil', 'Générer un questionnaire']}>
      {/* External candidate banner */}
      {hasExternalCandidate && (
        <div className="bg-[#3B82F6]/5 border border-[#3B82F6]/20 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="external-candidate-banner">
          <div className="w-8 h-8 bg-[#3B82F6]/10 rounded-full flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-[#3B82F6]">B</span>
          </div>
          <div>
            <p className="text-sm font-medium text-white">Test pour {externalPrenom} {externalNom}</p>
            <p className="text-xs text-slate-400">Depuis Boond Manager — le test sera lancé automatiquement après la génération</p>
          </div>
        </div>
      )}

      {/* Mode Selection */}
      {!generatedData && (
        <div className="mb-8">
          <div className="flex gap-4">
            <button onClick={() => setMode('description')} data-testid="mode-description-button"
              className={`eva-card flex-1 cursor-pointer transition-all ${mode === 'description' ? 'ring-2 ring-[#0B132B]' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-slate-500/15 rounded-lg flex items-center justify-center">
                  <Upload size={24} className="text-[#3A506B]" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-white">Appel d'offres</h3>
                  <p className="text-xs text-slate-400">Description de poste ou fichier</p>
                </div>
              </div>
            </button>
            <button onClick={() => setMode('generic')} data-testid="mode-generic-button"
              className={`eva-card flex-1 cursor-pointer transition-all ${mode === 'generic' ? 'ring-2 ring-[#0B132B]' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                  <Sparkles size={24} className="text-white" strokeWidth={1.5} />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-white">Générique</h3>
                  <p className="text-xs text-slate-400">Fonction, niveau et compétences</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Generic Mode Form */}
      {!generatedData && mode === 'generic' && (
        <div className="eva-card mb-6" data-testid="generic-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="eva-label">Fonction / Poste *</label>
              <AutocompleteInput value={fonction} onChange={(val) => setFonction(val)}
                endpoint="/autocomplete/fonctions" className="eva-input" dataTestId="fonction-input" />
            </div>
            <div>
              <label className="eva-label">Niveau *</label>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)} className="eva-input" data-testid="niveau-select">
                <option value="">Sélectionner</option>
                <option value="Junior">Junior</option>
                <option value="Confirmé">Confirmé</option>
                <option value="Senior">Senior</option>
                <option value="Expert">Expert</option>
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="eva-label">Compétences clés (optionnel)</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {competences.map((comp, i) => (
                <input key={i} type="text" value={comp}
                  onChange={(e) => { const c = [...competences]; c[i] = e.target.value; setCompetences(c); }}
                  className="eva-input" placeholder={`Compétence ${i + 1}`} data-testid={`competence-${i}`} />
              ))}
            </div>
          </div>
          <button onClick={handleGenerate} disabled={loading} className="eva-btn-primary" data-testid="generate-button">
            <Sparkles size={16} className={`inline mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Génération en cours...' : 'Générer le questionnaire'}
          </button>
        </div>
      )}

      {/* Description Mode Form */}
      {!generatedData && mode === 'description' && (
        <div className="eva-card mb-6" data-testid="description-form">
          <div className="mb-4">
            <label className="eva-label">Description du poste</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              className="eva-input min-h-[120px]" placeholder="Collez ici la description du poste ou les compétences recherchées..." data-testid="description-textarea" />
          </div>
          <div className="mb-4">
            <label className="eva-label">Fichier (optionnel)</label>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragging ? 'border-[#0B132B] bg-white/5' : 'border-white/10 hover:border-[#6C757D]'}`}
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
              <Upload size={24} className="text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-400">
                {file ? file.name : 'Glissez-déposez un fichier ou cliquez pour parcourir'}
              </p>
              <input type="file" onChange={(e) => setFile(e.target.files[0])}
                className="mt-2 text-sm" data-testid="file-input" accept=".pdf,.doc,.docx,.txt" />
            </div>
          </div>
          <button onClick={handleGenerate} disabled={loading} className="eva-btn-primary" data-testid="generate-button">
            <Sparkles size={16} className={`inline mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Génération en cours...' : 'Générer le questionnaire'}
          </button>
        </div>
      )}

      {/* Generated Questions */}
      {generatedData && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">Questions générées</h3>
            <button onClick={handleGenerate} disabled={loading} className="eva-btn-secondary text-sm" data-testid="regenerate-all-button">
              <RotateCw size={16} className={`inline mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Régénération...' : 'Tout régénérer'}
            </button>
          </div>

          <div className="space-y-4 mb-8" data-testid="generated-questions">
            {generatedData.questions.map((question, index) => (
              <QuestionCard key={question.id} question={question} index={index} onRegenerate={handleRegenerateQuestion} />
            ))}
          </div>

          <ValidationForm
            fonction={fonction} setFonction={setFonction}
            niveau={niveau} setNiveau={setNiveau}
            typeQuestionnaire={typeQuestionnaire} setTypeQuestionnaire={setTypeQuestionnaire}
            client={client} setClient={setClient}
            interlocuteurClient={interlocuteurClient} setInterlocuteurClient={setInterlocuteurClient}
            refAo={refAo} onRefAoChange={handleRefAoChange}
            duplicateInfo={duplicateInfo} errors={errors}
            saving={saving} onSave={handleSave} onLaunchTest={handleLaunchTest}
          />
        </div>
      )}
      {/* Duplicate AO Popup */}
      {duplicatePopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="duplicate-ao-popup">
          <div className="bg-[#0B132B] rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#F59E0B]/10 rounded-full flex items-center justify-center shrink-0">
                <Sparkles size={20} className="text-[#F59E0B]" />
              </div>
              <h3 className="text-lg font-semibold text-white">Questionnaire existant</h3>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              {duplicatePopup.ref_ao
                ? `Un questionnaire a déjà été généré pour l'appel d'offres ${duplicatePopup.ref_ao}`
                : "Un questionnaire a déjà été généré pour cet appel d'offres"
              }
            </p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-white">{duplicatePopup.fonction}</p>
              <p className="text-xs text-slate-400 mt-1">
                {duplicatePopup.niveau}
                {duplicatePopup.client && ` — ${duplicatePopup.client}`}
                {duplicatePopup.test_count > 0 && ` — ${duplicatePopup.test_count} test(s) passé(s)`}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDuplicatePopup(null)}
                className="flex-1 eva-btn-secondary"
                data-testid="duplicate-popup-continue"
              >
                Continuer quand même
              </button>
              <button
                onClick={() => navigate(`/lancer-test/${duplicatePopup.id}`)}
                className="flex-1 eva-btn-primary"
                data-testid="duplicate-popup-use-existing"
              >
                Lancer le test existant
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
